import { sagaCreator, stateSelector } from 'redux-saga-kit';
import { call, put, select } from 'redux-saga/effects';
import {
  getPaymentsWithAmountGreaterThanZero,
  validatePaymentsAmount,
  ValidationResultType,
} from '../../utils/CheckoutUtils';
import {
  addPayment,
  placeOrder,
  placeOrderResult,
  removePayment,
  setPayments,
  orderConfirmed,
  setCheckoutType,
  selectPaymentType,
  resetOrderResult,
  updatePayment,
  indicateWaitingForResponseFromServer,
  addCashierPayment,
  setCashierInitializing,
  setPendingPayment,
} from '../engine/actions/CheckoutActions';
import {
  addComment,
  cartUpsert,
  changeDeliveryFee,
  orderItemChangeCount,
  sendCommentsToServer,
  setCommentsServerTimestamp,
  setHasUnsavedComments,
  setManagerDiscount,
} from '../engine/actions/OrderActions';
import { derivedOrderData } from '../engine/selectors/derivedOrderData';
import { openrestHelpers } from '@wix/wixrest-utils';
import _ from 'lodash';
import BILogger from '../../utils/BILogger';
import {
  clearCustomerSession,
  displayModal,
  fullOrganizationSuccess,
  hideModal,
} from '../engine/actions/SessionActions';
import { CASHIER_RETURN_URL, CheckoutTypes, Modals } from '../../utils/constants';
import { deliveryTypeSelector } from '../engine/selectors/deliveryTypeSelector';
import { v4 } from 'uuid';
import { safeValidate, transformCreditCardData } from '../../utils/CashierUtils';

const { OrderItem } = openrestHelpers;

function onOrderConfirmed() {
  return function*({ payload }) {
    const { commentStr } = payload;

    try {
      yield put(addComment({ text: commentStr, userId: '' }));
      yield put(sendCommentsToServer());
    } catch (e) {
      console.warn('Error saving customer comments');
    }

    yield put(clearCustomerSession());
  };
}

function onPaymentsChange() {
  return function*(action) {
    if (action.type === removePayment.toString()) {
      //todo masha separate handlers
      const { amount, type } = action.payload.payment;
      const organizationId = yield select(stateSelector('session.organization.id'));
      BILogger.callCenterCheckoutClickOnRemovePaymentMethod({
        amount,
        organization_id: organizationId,
        pmnt_method: type,
      });
    }
    const { priceComponents } = derivedOrderData(yield select());
    const payments = yield select(stateSelector('checkout.payments'));
    const checkoutType = yield select(stateSelector('checkout.checkoutType'));
    const hasNoItemsInCart = _.isEmpty(yield select(stateSelector('order.orderItems')));
    const isSinglePayment = payments.length === 1;

    if (isSinglePayment) {
      yield put(
        setPayments({
          paymentsValue: [
            {
              ...payments[0],
              manualAmount: priceComponents.total, // if  only one payment we cant PayPerDish
            },
          ],
        }),
      );
    }

    if (checkoutType === CheckoutTypes.PAY_PER_DISH && (isSinglePayment || hasNoItemsInCart)) {
      yield put(setCheckoutType({ checkoutType: CheckoutTypes.MANUALLY }));
    }
  };
}

function onSendCommentsToServer({ restClient, PhoneUtils }) {
  return function*() {
    const organizationId = yield select(stateSelector('session.organization.id'));
    const accessToken = yield select(stateSelector('session.accessToken'));
    const { phone } = yield select(stateSelector('order.customer'));
    const countryCode = yield select(stateSelector('session.organization.address.countryCode'));
    const intPhone = PhoneUtils.internationalizePhone(phone, countryCode);
    const customerId = encodeURIComponent(`tel:${intPhone}`);
    const comments = yield select(stateSelector('order.comments'));
    const commentsServerTimestamp = yield select(stateSelector('order.commentsServerTimestamp'));
    const hasUnsavedComments = yield select(stateSelector('order.hasUnsavedComments'));

    if (!hasUnsavedComments) {
      return;
    }

    const saveCommentsResponse = yield call(
      restClient,
      `/organizations/${organizationId}/customers/${customerId}/secrets`,
      'put',
      {
        modified: commentsServerTimestamp,
        properties: {
          'com.wix.restaurants': JSON.stringify({ comments }),
        },
      },
      {
        type: 'Bearer',
        credentials: accessToken,
      },
    );

    yield put(setCommentsServerTimestamp({ commentsServerTimestamp: _.get(saveCommentsResponse, 'modified') }));
    yield put(setHasUnsavedComments({ hasUnsavedComments: false }));
  };
}

function onPlaceOrder({ restClient }) {
  return function*() {
    const { priceComponents, orderCharges, displayableDiscounts } = derivedOrderData(yield select());
    const payments = yield select(stateSelector('checkout.payments'));
    const result = validatePaymentsAmount(priceComponents.total, payments);
    if (result.type === ValidationResultType.OVER_PAY || result.type === ValidationResultType.UNDER_PAY) {
      return yield put(placeOrderResult({ error: result, orderInfo: null }));
    }

    const relevantPayments = getPaymentsWithAmountGreaterThanZero(payments);
    yield put(setPayments({ paymentsValue: relevantPayments }));

    const organization = yield select(stateSelector('session.organization'));
    const msid = yield select(stateSelector('session.msid'));
    const customer = yield select(stateSelector('order.customer'));
    const comment = yield select(stateSelector('order.comment'));
    const contact = _.pick(customer, ['firstName', 'lastName', 'email', 'phone']);
    const deliveryType = deliveryTypeSelector(yield select());
    const address = yield select(stateSelector('order.address'));
    const clonedOrderItems = _.cloneDeep(yield select(stateSelector('order.orderItems')));
    const orderItems = clonedOrderItems.map(orderItem => OrderItem.convertOrderItemToExternalOrderItem(orderItem));
    const platform = 'callcenter';
    const source = '';
    const idRequest = v4();

    const deliveryCharge = priceComponents.deliveryFee > 0 ? { charge: priceComponents.deliveryFee } : {};
    const order = {
      externalIds: { msid },
      comment,
      delivery: { address, type: deliveryType, ...deliveryCharge },
      contact,
      payments: relevantPayments,
      submitAt: null,
      price: priceComponents.total,
      orderCharges,
      orderItems,
      restaurantId: organization.id,
      status: 'new',
      locale: organization.locale,
      currency: organization.currency,
      developer: 'com.wix.restaurants',
      platform,
      source,
    };

    let isSuccess = false;
    let orderInfo = null;
    try {
      yield put(indicateWaitingForResponseFromServer({ isWaitingForResponseFromServer: true }));
      BILogger.submitOrderRequest({ msid, organizationId: organization.id, idRequest });
      orderInfo = yield call(postPlaceOrder(restClient), order);
      yield put(sendCommentsToServer());
      yield put(placeOrderResult({ error: null, orderInfo }));
      yield put(displayModal({ modal: Modals.ORDER_CONFIRMATION }));
      isSuccess = true;
    } catch (error) {
      yield put(placeOrderResult({ error: error.errorDescription, orderInfo: null }));
      yield put(displayModal({ modal: Modals.ORDER_PLACEMENT_ERROR }));
    }

    yield put(indicateWaitingForResponseFromServer({ isWaitingForResponseFromServer: false }));

    BILogger.submitOrderResponse({
      currency: organization.currency,
      deliveryCharges: priceComponents.deliveryFee,
      grandTotal: priceComponents.total,
      msid,
      numDiscounts: displayableDiscounts.filter(d => _.isNumber(d.amount) && d.amount !== 0).length,
      numItems: orderItems.reduce((acc, orderItem) => acc + (orderItem.count || 1), 0),
      orderMethod: deliveryType,
      organizationId: organization.id,
      idRequest,
      isSuccess,
      errorDescription: '',
      orderId: _.get(orderInfo, 'id', ''),
    });
  };
}

const postPlaceOrder = restClient =>
  function*(order) {
    const organizationId = yield select(stateSelector('session.organization.id'));
    const authorization = yield select(stateSelector('session.accessToken'));
    return yield call(restClient, `/organizations/${organizationId}/orders`, 'post', order, authorization);
  };

function* onSelectPaymentType({ payload }) {
  const organization_id = yield select(stateSelector('session.organization.id'));
  const payment_index = (stateSelector('checkout.payments').length - 1).toString();

  BILogger.callCenterCheckoutClickOnPaymentMethodSelection({
    organization_id,
    payment_index,
    pmnt_method: payload.paymentType,
  });
}

function* onSetCheckoutType({ payload }) {
  const organization_id = yield select(stateSelector('session.organization.id'));
  BILogger.callCenterCheckoutChooseCheckoutMethod({ organization_id, checkout_method: payload.checkoutType });
}
function* onUpdatePayment() {
  if (yield select(stateSelector('session'))) {
    yield put(resetOrderResult());
  }
}
function* removeUnsupportedPayments({ payload }) {
  const supportedPaymentTypes = _.get(payload, 'full.restaurant.paymentTypes', []);
  const payments = yield select(stateSelector('checkout.payments'));

  let foundInvalidPayment = false;

  for (let i = payments.length - 1; i >= 0; i--) {
    const payment = payments[i];
    if (!supportedPaymentTypes.includes(payment.type)) {
      yield put(removePayment({ paymentIndex: i, payment }));
      foundInvalidPayment = true;
    }
  }

  if (foundInvalidPayment) {
    yield put(displayModal({ modal: Modals.SETTINGS_CHANGED }));
  }
}

function onAddCashierPayment({ getCashierApi }) {
  return function*({ payload }) {
    const { paymentMethod } = payload;

    const instance = yield select(stateSelector('session.instance'));
    const accountId = `${instance.appDefId}:${instance.instanceId}`;

    yield put(setCashierInitializing({ isCashierInitializing: true }));

    const cashierApi = yield call(getCashierApi);
    const { isValid } = yield call(safeValidate, cashierApi);

    const paymentMetadata = yield select(stateSelector('cashier.paymentMetadata'));
    const { title: paymentMethodTitle } = paymentMetadata.methods.find(pm => pm.id === paymentMethod);

    if (isValid) {
      const { detailsId: paymentDetailsId, cardPublicData } = yield call(cashierApi.initializePayment);

      const paymentValue = {
        type: 'cashier',
        paymentMethod,
        paymentMethodTitle,
        accountId,
        paymentDetailsId,
        returnUrl: CASHIER_RETURN_URL,
      };

      if (cardPublicData) {
        paymentValue.creditCard = transformCreditCardData(cardPublicData);
      }

      yield put(addPayment({ paymentValue }));
      yield put(hideModal({ modal: Modals.CASHIER_WIDGET_MODAL }));
      yield put(setPendingPayment({ hasPendingPayment: false }));
    }

    yield put(setCashierInitializing({ isCashierInitializing: false }));
  };
}
////////////// default export

export default sagaCreator(
  Object.assign({
    [selectPaymentType]: { handler: onSelectPaymentType, noParams: true },
    [addPayment]: { handler: onPaymentsChange },
    [removePayment]: { handler: onPaymentsChange },
    [cartUpsert]: { handler: onPaymentsChange },
    [orderItemChangeCount]: { handler: onPaymentsChange },
    [setManagerDiscount]: { handler: onPaymentsChange },
    [changeDeliveryFee]: { handler: onPaymentsChange },
    [placeOrder]: { handler: onPlaceOrder },
    [orderConfirmed]: { handler: onOrderConfirmed },
    [setCheckoutType]: { handler: onSetCheckoutType, noParams: true },
    [fullOrganizationSuccess]: { handler: removeUnsupportedPayments, noParams: true },
    [updatePayment]: { handler: onUpdatePayment, noParams: true },
    [sendCommentsToServer]: { handler: onSendCommentsToServer },
    [addCashierPayment]: { handler: onAddCashierPayment },
  }),
);
